/**
 * ObjectManagerPanel — colony list with virtualized rows, hover-to-highlight,
 * compact mode, shift/ctrl multi-select, bulk delete, and double-click rename.
 *
 * Two layout modes:
 *   - Floating (default): keeps the original draggable/resizable Rnd window.
 *     Used in multi-image analysis where the panel needs to float over the
 *     timeline view.
 *   - Docked: when `docked=true`, renders inline (no Rnd, fills the parent
 *     container). Used in single-image mode as the always-visible side panel.
 *
 * Hover-highlight contract: parent supplies `onHoverObject(objId | null)`.
 * Segmentationbeta.js stashes that in `hoveredObjectIdRef` and re-renders the
 * canvas mask layer with brighter stroke on hovered + dim on others.
 *
 * Area badge contract: parent supplies `objectMasksForFrame` keyed by objId.
 * We compute area by summing the mask buffer once per (objId, mask buffer)
 * pair and memoize on the buffer identity.
 */
import React, {
  useState, useEffect, useMemo, useRef, useCallback,
} from 'react';
import {
  Box, Typography, IconButton, Tooltip, Paper, TextField, Avatar,
  Popover, Menu, MenuItem, ListItemText, Divider, Button, Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaletteIcon from '@mui/icons-material/Palette';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Rnd } from 'react-rnd';

const DEFAULT_COLORS = [
  '#ff5252', '#4caf50', '#2196f3', '#ffeb3b',
  '#e91e63', '#00bcd4', '#ff9800', '#9c27b0',
  '#8bc34a', '#03a9f4', '#f44336', '#607d8b',
];

const PICKER_COLORS = [
  '#ff5252', '#4caf50', '#2196f3', '#ffeb3b',
  '#e91e63', '#00bcd4', '#ff9800', '#9c27b0',
  '#8bc34a', '#03a9f4', '#f44336', '#607d8b',
  '#ff4081', '#69f0ae', '#40c4ff', '#ffd740',
  '#f50057', '#00e676', '#00b0ff', '#ffab00',
];

const HEADER_HEIGHT = 56;
const ROW_HEIGHT = 36;
const COMPACT_THRESHOLD = 50; // auto-switch to compact rows above this many colonies

const getColorByObjectId = (objectId, objectMeta) => {
  if (objectMeta[objectId]?.customColor) return objectMeta[objectId].customColor;
  return DEFAULT_COLORS[(objectId - 1) % DEFAULT_COLORS.length];
};

// Mask buffer → pixel count cache keyed by the Uint8Array's underlying buffer
// (same buffer → same area, so we compute once even when the parent re-renders).
const areaCache = new WeakMap();
const maskArea = (maskData) => {
  if (!maskData || !maskData.mask) return 0;
  const buf = maskData.mask.buffer;
  const cached = areaCache.get(buf);
  if (cached != null) return cached;
  let count = 0;
  const m = maskData.mask;
  for (let i = 0; i < m.length; i++) if (m[i]) count++;
  areaCache.set(buf, count);
  return count;
};

const ObjectManagerPanel = ({
  objectMeta,
  setObjectMeta,
  deleteObject,
  currentObjectId,
  setCurrentObjectId,
  currentFrameIndex,
  objectMasksForFrame,
  showSelectExisting = true,
  // New props:
  docked = false,
  onHoverObject,
  onAddObject,
  hiddenObjectIds, // optional Set; when provided, visibility toggle is wired
  setHiddenObjectIds,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [size, setSize] = useState({ width: 300, height: 520 });
  const [position, setPosition] = useState({
    x: typeof window !== 'undefined' ? window.innerWidth - 320 : 10,
    y: 250,
  });
  const [lastExpandedHeight, setLastExpandedHeight] = useState(520);
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);
  const [colorPickerObjectId, setColorPickerObjectId] = useState(null);
  const [selectMenuAnchor, setSelectMenuAnchor] = useState(null);
  // Multi-selection (shift-click ranges, ctrl/cmd-click toggle).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const lastClickedIdRef = useRef(null);
  // Inline rename state — { id, value } when active.
  const [renameState, setRenameState] = useState(null);
  useEffect(() => {
    // Reset multi-selection state when the user moves frames so a stale
    // selection from a different frame doesn't survive.
    setSelectedIds(new Set());
    lastClickedIdRef.current = null;
  }, [currentFrameIndex]);

  // ── Object lists ─────────────────────────────────────────────────────────
  const allExistingObjects = useMemo(
    () => Object.entries(objectMeta)
      .map(([objId, meta]) => ({ id: parseInt(objId, 10), name: meta.name || `Object ${objId}` }))
      .sort((a, b) => b.id - a.id),
    [objectMeta],
  );
  const selectableObjects = useMemo(
    () => allExistingObjects.filter(o => !objectMasksForFrame || !objectMasksForFrame[o.id]),
    [allExistingObjects, objectMasksForFrame],
  );

  const filteredObjects = useMemo(() => {
    const q = searchText.toLowerCase();
    // Union the ids from BOTH objectMeta and objectMasksForFrame so a colony
    // that landed in the mask map (e.g. SAM2's add_prompt response sent a new
    // obj_id) but never got an explicit meta entry still appears as a row.
    // Without this the list would show fewer colonies than the visualization.
    const ids = new Set();
    Object.keys(objectMeta || {}).forEach(k => ids.add(parseInt(k, 10)));
    if (objectMasksForFrame) {
      Object.keys(objectMasksForFrame).forEach(k => ids.add(parseInt(k, 10)));
    }
    const out = [];
    for (const id of ids) {
      if (!Number.isFinite(id) || id <= 0) continue;
      const meta = objectMeta[id] || objectMeta[String(id)] || { name: `Object ${id}` };
      if (q && !(meta.name || '').toLowerCase().includes(q)) continue;
      out.push([id, meta]);
    }
    out.sort((a, b) => b[0] - a[0]);
    return out;
  }, [objectMeta, objectMasksForFrame, searchText]);

  // Auto-compact when the list grows long; the user doesn't need huge rows
  // when they're skimming hundreds of colonies.
  const compact = filteredObjects.length >= COMPACT_THRESHOLD;
  const rowHeight = compact ? 28 : ROW_HEIGHT;

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleAddObject = useCallback(() => {
    if (onAddObject) {
      onAddObject();
      return;
    }
    // Legacy fallback for callers that haven't wired onAddObject — should be
    // unreachable in the current codebase but kept so we don't break anyone.
    const existingIds = Object.keys(objectMeta).map(Number);
    const newId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
    setObjectMeta((prev) => ({ ...prev, [newId]: { name: `Object ${newId}` } }));
    setCurrentObjectId(newId);
  }, [onAddObject, objectMeta, setObjectMeta, setCurrentObjectId]);

  const beginRename = useCallback((id) => {
    const currentName = objectMeta[id]?.name || `Object ${id}`;
    setRenameState({ id, value: currentName });
  }, [objectMeta]);

  const commitRename = useCallback(() => {
    if (!renameState) return;
    const { id, value } = renameState;
    const v = (value || '').trim();
    setRenameState(null);
    if (!v) return; // ignore empty
    const exists = Object.entries(objectMeta).some(
      ([oid, m]) => parseInt(oid, 10) !== id && (m.name || '').toLowerCase() === v.toLowerCase(),
    );
    if (exists) return;
    setObjectMeta(prev => ({ ...prev, [id]: { ...prev[id], name: v } }));
  }, [renameState, objectMeta, setObjectMeta]);

  const handleRowClick = useCallback((id, e) => {
    if (e.shiftKey && lastClickedIdRef.current != null) {
      // Range select between lastClicked and id, in the visible list order.
      const ids = filteredObjects.map(([oid]) => oid);
      const a = ids.indexOf(lastClickedIdRef.current);
      const b = ids.indexOf(id);
      if (a >= 0 && b >= 0) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        const range = ids.slice(lo, hi + 1);
        setSelectedIds(prev => {
          const next = new Set(prev);
          range.forEach(r => next.add(r));
          return next;
        });
      }
    } else if (e.metaKey || e.ctrlKey) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
      });
    } else {
      // Plain click: clear multi-select and just change the current object.
      setSelectedIds(new Set());
      setCurrentObjectId(id);
    }
    lastClickedIdRef.current = id;
  }, [filteredObjects, setCurrentObjectId]);

  const handleBulkDelete = useCallback(() => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} object${selectedIds.size === 1 ? '' : 's'}?`)) return;
    [...selectedIds].forEach(id => deleteObject(id));
    setSelectedIds(new Set());
  }, [selectedIds, deleteObject]);

  const handleSingleDelete = useCallback((id) => {
    deleteObject(id);
    setSelectedIds(prev => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    const remaining = Object.keys(objectMeta)
      .map(Number)
      .filter(oid => oid !== id)
      .sort((a, b) => a - b);
    setCurrentObjectId(remaining[0] ?? 1);
  }, [deleteObject, objectMeta, setCurrentObjectId]);

  const toggleVisibility = useCallback((id) => {
    if (!setHiddenObjectIds) return;
    setHiddenObjectIds(prev => {
      const next = new Set(prev || []);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, [setHiddenObjectIds]);

  const handleColorPickerOpen = (e, objId) => {
    e.stopPropagation();
    setColorPickerAnchor(e.currentTarget);
    setColorPickerObjectId(objId);
  };
  const handleColorPickerClose = () => {
    setColorPickerAnchor(null);
    setColorPickerObjectId(null);
  };
  const handleColorChange = (color) => {
    if (!colorPickerObjectId) return;
    setObjectMeta(prev => ({
      ...prev,
      [colorPickerObjectId]: { ...prev[colorPickerObjectId], customColor: color },
    }));
  };

  // ── Row component ───────────────────────────────────────────────────────
  // Row is rendered as a plain JSX subtree (not a custom component) so its
  // element type stays a stable <div> across renders. Wrapping the JSX in a
  // useCallback'd Row component made every row click change Row's function
  // identity (deps included currentObjectId), which forced React to unmount
  // and remount every row on each click. The browser then never saw two
  // clicks land on the same DOM element, so onDoubleClick (rename) never
  // fired. Building a JSX array via useMemo lets React reconcile by key
  // and reuse the existing DOM nodes.
  const renderOneRow = ({ id, meta }) => {
    const isActive = id === currentObjectId;
    const isSelectedBulk = selectedIds.has(id);
    const color = getColorByObjectId(id, objectMeta);
    const maskData = objectMasksForFrame ? objectMasksForFrame[id] : null;
    const area = maskData ? maskArea(maskData) : 0;
    const isHidden = hiddenObjectIds ? hiddenObjectIds.has(id) : false;
    const isRenaming = renameState && renameState.id === id;

    return (
      <div
        onMouseEnter={() => onHoverObject && onHoverObject(id)}
        onMouseLeave={() => onHoverObject && onHoverObject(null)}
      >
        <Paper
          elevation={0}
          tabIndex={0}
          onClick={(e) => handleRowClick(id, e)}
          onDoubleClick={(e) => { e.stopPropagation(); beginRename(id); }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: compact ? 0.2 : 0.5,
            mx: 0.5,
            mb: compact ? 0.25 : 0.5,
            height: rowHeight - (compact ? 4 : 6),
            borderRadius: 1,
            bgcolor: isActive ? 'primary.main' : isSelectedBulk ? '#374151' : '#2c2c2e',
            color: isActive ? '#fff' : '#ddd',
            cursor: 'pointer',
            transition: 'background-color 0.1s',
            opacity: isHidden ? 0.45 : 1,
            outline: isSelectedBulk ? '1px solid #64b5f6' : 'none',
            '&:hover': {
              bgcolor: isActive ? 'primary.dark' : '#3a3a3d',
            },
          }}
        >
          <Avatar sx={{
            bgcolor: color,
            width: compact ? 18 : 22,
            height: compact ? 18 : 22,
            fontSize: compact ? 10 : 11,
            flexShrink: 0,
          }}>
            {id}
          </Avatar>
          {isRenaming ? (
            <TextField
              autoFocus
              variant="standard"
              size="small"
              value={renameState.value}
              onChange={(e) => setRenameState(s => ({ ...s, value: e.target.value }))}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                else if (e.key === 'Escape') setRenameState(null);
                e.stopPropagation();
              }}
              onClick={(e) => e.stopPropagation()}
              sx={{ flex: 1, '& input': { color: '#fff', py: 0, fontSize: '0.85rem' } }}
            />
          ) : (
            <Typography
              variant="caption"
              sx={{
                flex: 1, minWidth: 0,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                fontSize: compact ? '0.72rem' : '0.82rem',
              }}
              title={meta.name || `Object ${id}`}
            >
              {meta.name || `Object ${id}`}
            </Typography>
          )}
          {area > 0 && (
            <Typography
              variant="caption"
              sx={{
                color: isActive ? '#e3f2fd' : '#888',
                fontSize: compact ? '0.65rem' : '0.7rem',
                fontFamily: 'monospace',
                ml: 0.5,
              }}
              title={`Area: ${area.toLocaleString()} px`}
            >
              {area >= 1000 ? `${(area / 1000).toFixed(1)}k` : area}
            </Typography>
          )}
          {/* Species pill — SupCon-DINO centroid softmax (v2). The chip
              shows top-1 species + confidence; the tooltip surfaces the
              full 6-class distribution. v1 (detector-head top-1) is still
              persisted on the mask for backward compatibility but is no
              longer rendered. */}
          {meta.species_top1_v2 && (
            <Tooltip
              title={
                meta.species_scores_v2
                  ? Object.entries(meta.species_scores_v2)
                      .sort((a, b) => b[1] - a[1])
                      .map(([n, s]) => `${n}: ${(s * 100).toFixed(0)}%`)
                      .join('\n')
                  : `${meta.species_top1_v2}: ${((meta.species_top1_score_v2 || 0) * 100).toFixed(0)}%`
              }
            >
              <Chip
                size="small"
                label={`${meta.species_top1_v2} ${Math.round(
                  (meta.species_scores_v2
                    ? meta.species_scores_v2[meta.species_top1_v2]
                    : meta.species_top1_score_v2) * 100 || 0,
                )}%`}
                sx={{
                  height: compact ? 16 : 18,
                  fontSize: compact ? '0.6rem' : '0.65rem',
                  ml: 0.5,
                  bgcolor: isActive ? 'rgba(255,255,255,0.18)' : 'rgba(186,104,200,0.15)',
                  color: isActive ? '#fff' : '#ce93d8',
                  border: '1px solid rgba(186,104,200,0.35)',
                  '& .MuiChip-label': { px: 0.6 },
                }}
              />
            </Tooltip>
          )}
          {/* Find-Similar adds a similarity score instead of a species. */}
          {meta.similarity != null && !meta.species_top1 && (
            <Tooltip title={`Cosine similarity to exemplar: ${(meta.similarity * 100).toFixed(0)}%`}>
              <Chip
                size="small"
                label={`sim ${Math.round(meta.similarity * 100)}%`}
                sx={{
                  height: compact ? 16 : 18,
                  fontSize: compact ? '0.6rem' : '0.65rem',
                  ml: 0.5,
                  bgcolor: 'rgba(129,199,132,0.15)',
                  color: '#81c784',
                  border: '1px solid rgba(129,199,132,0.35)',
                  '& .MuiChip-label': { px: 0.6 },
                }}
              />
            </Tooltip>
          )}
          {setHiddenObjectIds && (
            <Tooltip title={isHidden ? 'Show mask' : 'Hide mask'}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); toggleVisibility(id); }}
                sx={{ p: 0.25, color: isHidden ? '#666' : '#90caf9' }}
              >
                {isHidden ? <VisibilityOffIcon fontSize="inherit" /> : <VisibilityIcon fontSize="inherit" />}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Color">
            <IconButton
              size="small"
              onClick={(e) => handleColorPickerOpen(e, id)}
              sx={{ p: 0.25, color: '#ffa726' }}
            >
              <PaletteIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleSingleDelete(id); }}
              sx={{ p: 0.25, color: '#f44336' }}
            >
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Paper>
      </div>
    );
  };

  // ── Inner content (header + search + list), reused by both layouts ──────
  const Content = (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0,
      bgcolor: '#1c1c1e', color: '#fff', borderRadius: docked ? 0 : '8px', overflow: 'hidden',
    }}>
      {/* Header */}
      <Box
        className={docked ? undefined : 'drag-handle'}
        sx={{
          px: 1.5, py: 0.75,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          bgcolor: '#111', borderBottom: '1px solid #333',
          height: HEADER_HEIGHT, flexShrink: 0,
          cursor: docked ? 'default' : 'move',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Colonies</Typography>
          <Typography variant="caption" sx={{ color: '#888' }}>
            {filteredObjects.length}
            {selectedIds.size > 0 ? ` · ${selectedIds.size} selected` : ''}
          </Typography>
        </Box>
        <Box>
          {selectedIds.size > 0 && (
            <Tooltip title={`Delete ${selectedIds.size} selected`}>
              <IconButton size="small" onClick={handleBulkDelete} sx={{ color: '#f44336' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Add object (+)">
            <IconButton size="small" onClick={handleAddObject} sx={{ color: '#90caf9' }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {showSelectExisting && selectableObjects.length > 0 && (
            <Tooltip title="Select from other frames">
              <IconButton size="small" onClick={(e) => setSelectMenuAnchor(e.currentTarget)} sx={{ color: '#4caf50' }}>
                <PlaylistAddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!docked && (
            <IconButton
              size="small"
              onClick={() => {
                setCollapsed(prev => {
                  const next = !prev;
                  if (next) {
                    setLastExpandedHeight(h => size.height || h);
                    setSize(s => ({ ...s, height: HEADER_HEIGHT }));
                  } else {
                    setSize(s => ({ ...s, height: Math.max(lastExpandedHeight, 320) }));
                  }
                  return next;
                });
              }}
              sx={{ color: '#ccc' }}
            >
              {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Plain flex container instead of MUI Collapse. Collapse wraps children
          in a div that doesn't propagate height, so the list's overflowY:auto
          had no fixed height to constrain against — that's why scrolling
          appeared broken. Conditionally render with sx display=none when
          collapsed; layout reflows cleanly. */}
      <Box sx={{ flex: 1, minHeight: 0, display: collapsed ? 'none' : 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Search */}
          <Box sx={{ px: 1.5, py: 0.75 }}>
            <TextField
              fullWidth variant="outlined" size="small"
              placeholder="Search…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                input: { color: '#fff', py: 0.5 },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#444' },
                  '&:hover fieldset': { borderColor: '#666' },
                  '&.Mui-focused fieldset': { borderColor: '#1976d2' },
                },
              }}
            />
          </Box>

          {/* Plain scrollable list. Past iterations tried react-window
              virtualization but the height handoff between the panel chrome
              and the FixedSizeList kept losing rows. Plain rendering is fast
              enough for thousands of colonies and behaves predictably. */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              py: 0.5,
              // Visible scrollbar (Chrome / Edge / Safari). MUI's default
              // dark theme uses near-invisible thumbs which is what made the
              // list feel like it couldn't scroll.
              '&::-webkit-scrollbar': { width: 8 },
              '&::-webkit-scrollbar-track': { bgcolor: '#1a1a1a' },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: '#444', borderRadius: 4,
                '&:hover': { bgcolor: '#555' },
              },
            }}
          >
            {filteredObjects.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#888', textAlign: 'center', py: 2 }}>
                {searchText ? 'No matches.' : 'No colonies yet. Click + to add one.'}
              </Typography>
            ) : (
              filteredObjects.map(([id, meta]) => (
                <React.Fragment key={id}>{renderOneRow({ id, meta })}</React.Fragment>
              ))
            )}
          </Box>

          {/* Footer hints */}
          <Box sx={{
            px: 1.5, py: 0.5, borderTop: '1px solid #2a2a2a',
            fontSize: '0.65rem', color: '#666',
          }}>
            Shift-click range · Ctrl/Cmd-click multi · Double-click to rename · <b>+</b> hotkey
          </Box>
        </Box>
      </Box>

      {/* Select Existing Object Menu */}
      <Menu
        anchorEl={selectMenuAnchor}
        open={Boolean(selectMenuAnchor)}
        onClose={() => setSelectMenuAnchor(null)}
        sx={{ zIndex: 1600 }}
        PaperProps={{ sx: { bgcolor: '#1e1e1e', color: '#fff', minWidth: 200, maxHeight: 400 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ color: '#90caf9', fontWeight: 'bold' }}>
            Select existing object
          </Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            Objects from other frames
          </Typography>
        </Box>
        <Divider sx={{ bgcolor: '#333' }} />
        {selectableObjects.length === 0 ? (
          <MenuItem disabled>
            <ListItemText primary="No objects available"
              primaryTypographyProps={{ sx: { color: '#888', fontSize: '0.9rem' } }} />
          </MenuItem>
        ) : (
          selectableObjects.map(obj => (
            <MenuItem
              key={obj.id}
              onClick={() => { setCurrentObjectId(obj.id); setSelectMenuAnchor(null); }}
              sx={{ '&:hover': { bgcolor: '#2c2c2e' } }}
            >
              <Avatar sx={{
                bgcolor: getColorByObjectId(obj.id, objectMeta),
                width: 24, height: 24, fontSize: 12, mr: 1.5,
              }}>{obj.id}</Avatar>
              <ListItemText primary={obj.name}
                primaryTypographyProps={{ sx: { color: '#fff', fontSize: '0.9rem' } }} />
            </MenuItem>
          ))
        )}
      </Menu>

      {/* Color Picker Popover */}
      <Popover
        open={Boolean(colorPickerAnchor)}
        anchorEl={colorPickerAnchor}
        onClose={handleColorPickerClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 1600 }}
      >
        <Box sx={{ p: 2, bgcolor: '#1e1e1e' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#fff' }}>Choose color</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
            {PICKER_COLORS.map(color => (
              <Box
                key={color}
                onClick={() => { handleColorChange(color); handleColorPickerClose(); }}
                sx={{
                  width: 32, height: 32, bgcolor: color, borderRadius: 1, cursor: 'pointer',
                  border: '2px solid transparent',
                  '&:hover': { border: '2px solid #fff', transform: 'scale(1.1)' },
                  transition: 'all 0.15s',
                }}
              />
            ))}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mb: 0.5 }}>
              Or enter custom color:
            </Typography>
            <input
              type="color"
              onChange={(e) => handleColorChange(e.target.value)}
              style={{
                width: '100%', height: 36, border: '1px solid #555', borderRadius: 4,
                cursor: 'pointer', backgroundColor: '#2c2c2e',
              }}
            />
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="small" onClick={handleColorPickerClose} sx={{ color: '#ccc' }}>Done</Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </Box>
  );

  if (docked) {
    return <Box sx={{ height: '100%', width: '100%', minHeight: 0 }}>{Content}</Box>;
  }

  return (
    <Rnd
      size={{ width: size.width, height: size.height }}
      position={position}
      bounds="window"
      minWidth={220}
      minHeight={collapsed ? HEADER_HEIGHT : 320}
      dragHandleClassName="drag-handle"
      onDragStop={(e, d) => setPosition({ x: d.x, y: d.y })}
      onResizeStop={(e, direction, ref, delta, pos) => {
        setSize({ width: ref.offsetWidth, height: ref.offsetHeight });
        setPosition(pos);
      }}
      style={{
        zIndex: 1500,
        boxShadow: '0px 0px 10px rgba(0,0,0,0.5)',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      {Content}
    </Rnd>
  );
};

export default ObjectManagerPanel;
